/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { __, _x } from '@web-stories-wp/i18n';
import {
  NumericInput,
  Slider,
  Text,
  THEME_CONSTANTS,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useLutFilters } from '../../../../app/lutFilters';
import { Row } from '../../../form';
import { SimplePanel } from '../../panel';
import { inputContainerStyleOverride } from '../../shared';

const MIN_INTENSITY = 0;
const MAX_INTENSITY = 100;
const DEFAULT_INTENSITY = 100;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const PresetGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  max-height: 280px;
  overflow-y: auto;
  padding: 2px 4px 2px 2px;
`;

const PresetButton = styled.button`
  background: ${({ theme }) => theme.colors.bg.secondary};
  border: 2px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.border.defaultActive : 'transparent'};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.fg.primary};
  cursor: pointer;
  min-width: 0;
  overflow: hidden;
  padding: 0;
  text-align: left;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colors.border.defaultActive};
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }
`;

const PresetPreview = styled.img`
  aspect-ratio: 16 / 9;
  display: block;
  object-fit: cover;
  width: 100%;
`;

const NonePreview = styled.div`
  align-items: center;
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.colors.bg.primary};
  color: ${({ theme }) => theme.colors.fg.tertiary};
  display: flex;
  font-size: 20px;
  justify-content: center;
  width: 100%;
`;

const PresetName = styled.span`
  display: block;
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  padding: 6px 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Message = styled(Text)`
  color: ${({ $isError, theme }) =>
    $isError ? theme.colors.fg.negative : theme.colors.fg.tertiary};
`;

const CurrentSelection = styled(Text)`
  color: ${({ theme }) => theme.colors.fg.secondary};
`;

const IntensityRow = styled(Row)`
  gap: 12px;
  margin-bottom: 0;
`;

const IntensityControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IntensityLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.fg.secondary};
`;

const SliderContainer = styled.div`
  flex: 1;
`;

const IntensityInput = styled(NumericInput)`
  flex: 0 0 86px;
`;

function clampIntensity(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return DEFAULT_INTENSITY;
  }
  return Math.min(MAX_INTENSITY, Math.max(MIN_INTENSITY, numericValue));
}

const normalizeVersionId = (versionId) =>
  typeof versionId === 'string' ? versionId.toLowerCase() : '';

function ManagedLutFilterPanel({ selectedElements, pushUpdate }) {
  const { assetsByVersionId, filters, isLoading, loadError, resolutionError } =
    useLutFilters(({ state }) => state);
  const lutFilter = selectedElements[0]?.lutFilter;
  const versionId = normalizeVersionId(lutFilter?.versionId);
  const intensity = clampIntensity(lutFilter?.intensity);
  const hasLut = Boolean(versionId);
  const hasResolvedAsset = Boolean(assetsByVersionId?.[versionId]);
  const selectedFilter = filters.find(
    (filter) => normalizeVersionId(filter?.currentVersion?.id) === versionId
  );

  const updateLut = (nextVersionId, nextIntensity, submit) => {
    pushUpdate(
      {
        lutFilter: nextVersionId
          ? {
              versionId: nextVersionId,
              intensity: clampIntensity(nextIntensity),
            }
          : null,
      },
      submit
    );
  };

  const stageIntensity = (value) => updateLut(versionId, value, false);
  const commitIntensity = (event) =>
    updateLut(versionId, event.currentTarget.valueAsNumber, true);

  return (
    <SimplePanel name="lutFilter" title={__('LUT Filter', 'web-stories')}>
      <Content>
        {isLoading && (
          <Message size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}>
            {__('Loading filters…', 'web-stories')}
          </Message>
        )}
        {loadError && (
          <Message
            $isError
            role="alert"
            size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
          >
            {__('Unable to load LUT filters.', 'web-stories')}
          </Message>
        )}
        {!isLoading && !loadError && filters.length === 0 && (
          <Message size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}>
            {__('No LUT filters are available.', 'web-stories')}
          </Message>
        )}
        <PresetGrid
          role="group"
          aria-label={__('LUT filter presets', 'web-stories')}
        >
          <PresetButton
            type="button"
            $isSelected={!hasLut}
            aria-label={__('None', 'web-stories')}
            aria-pressed={!hasLut}
            onClick={() => updateLut('', DEFAULT_INTENSITY, true)}
          >
            <NonePreview aria-hidden="true">—</NonePreview>
            <PresetName>{__('None', 'web-stories')}</PresetName>
          </PresetButton>
          {filters.map((filter) => {
            const filterVersionId = normalizeVersionId(
              filter.currentVersion?.id
            );
            const isSelected = filterVersionId === versionId;

            return (
              <PresetButton
                key={filter.id}
                type="button"
                $isSelected={isSelected}
                aria-label={filter.name}
                aria-pressed={isSelected}
                onClick={() =>
                  updateLut(filterVersionId, filter.defaultIntensity, true)
                }
              >
                <PresetPreview
                  src={filter.previewAssetUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <PresetName title={filter.name}>{filter.name}</PresetName>
              </PresetButton>
            );
          })}
        </PresetGrid>
        {hasLut && !selectedFilter && (
          <CurrentSelection
            size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
          >
            {__(
              'This saved filter is archived or has been replaced. It remains available to this image.',
              'web-stories'
            )}
          </CurrentSelection>
        )}
        {hasLut && !hasResolvedAsset && resolutionError && (
          <Message
            $isError
            role="alert"
            size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
          >
            {__('Unable to resolve the saved LUT filter.', 'web-stories')}
          </Message>
        )}
        {hasLut && (
          <IntensityControl>
            <IntensityLabel
              size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL}
            >
              {__('Intensity', 'web-stories')}
            </IntensityLabel>
            <IntensityRow>
              <SliderContainer>
                <Slider
                  aria-label={__('LUT intensity', 'web-stories')}
                  min={MIN_INTENSITY}
                  max={MAX_INTENSITY}
                  majorStep={10}
                  minorStep={1}
                  value={intensity}
                  suffix={_x('%', 'Percentage', 'web-stories')}
                  handleChange={stageIntensity}
                  onBlur={commitIntensity}
                  onKeyUp={commitIntensity}
                  onMouseUp={commitIntensity}
                  onTouchEnd={commitIntensity}
                />
              </SliderContainer>
              <IntensityInput
                aria-label={__('LUT intensity in percent', 'web-stories')}
                unit={_x('%', 'Percentage', 'web-stories')}
                min={MIN_INTENSITY}
                max={MAX_INTENSITY}
                value={intensity}
                allowEmpty={false}
                canBeNegative={false}
                isFloat={false}
                containerStyleOverride={inputContainerStyleOverride}
                onChange={(event, value) => updateLut(versionId, value, false)}
                onBlur={(event, value) => updateLut(versionId, value, true)}
              />
            </IntensityRow>
          </IntensityControl>
        )}
      </Content>
    </SimplePanel>
  );
}

ManagedLutFilterPanel.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default ManagedLutFilterPanel;
